export type AccessPermissionKey = `${string}.${string}`;
export type AccessScopeRule = 'record' | 'any-psl' | 'global-only';

export interface AccessPsl {
  pslId: string;
  name: string;
  isActive: boolean;
  isGlobal: boolean;
}

export interface AccessRole {
  roleId: string;
  roleName: string;
  description: string;
  isActive: boolean;
  pslIds?: string[];
}

export interface AccessPermission {
  permissionKey: AccessPermissionKey;
  module: string;
  action: string;
  description: string;
  scopeRule: AccessScopeRule;
}

export interface AccessRolePermission {
  roleId: string;
  permissionKey: AccessPermissionKey;
}

export interface AccessRolePermissionGrant {
  roleId: string;
  permissionId: AccessPermissionKey;
  pslId: string;
}

export interface AccessRolePslPermission {
  roleId: string;
  pslId: string;
  permissionKey: AccessPermissionKey;
  effect: 'allow' | 'deny';
}

export interface AccessRoleAssignment {
  assignmentId: string;
  userId: string;
  roleId: string;
  pslId: string;
}

export interface AccessContext {
  userId: string;
  roleScopeVersion?: 3;
  roleMemberships?: { assignmentId: string; userId: string; roleId: string }[];
  permissionGrantVersion?: 4;
  rolePermissionGrants?: AccessRolePermissionGrant[];
  assignmentScopeVersion?: 2;
  profilePslIds?: string[];
  assignments: AccessRoleAssignment[];
  roles: AccessRole[];
  rolePermissions: AccessRolePermission[];
  rolePslPermissions: AccessRolePslPermission[];
  permissions: AccessPermission[];
  psls: AccessPsl[];
}

export interface AccessDecision {
  allowed: boolean;
  permissionKey: AccessPermissionKey;
  recordPslId: string | null;
  matchingRoles: string[];
  reason: 'granted' | 'no-assignment' | 'permission-not-granted' | 'inactive-role' | 'inactive-psl' | 'unknown-permission';
}

export const ACCESS_PERMISSIONS: AccessPermission[] = [
  ['Products.View', 'Products', 'View', 'View products', 'record'],
  ['Products.Create', 'Products', 'Create', 'Create products', 'record'],
  ['Products.Edit', 'Products', 'Edit', 'Edit products', 'record'],
  ['Products.Delete', 'Products', 'Delete', 'Delete products', 'record'],
  ['Products.Approve', 'Products', 'Approve', 'Approve products', 'record'],
  ['STP.View', 'STP', 'View', 'View standard test procedures', 'any-psl'],
  ['STP.Create', 'STP', 'Create', 'Create standard test procedures', 'any-psl'],
  ['STP.Edit', 'STP', 'Edit', 'Edit standard test procedures', 'any-psl'],
  ['STP.Approve', 'STP', 'Approve', 'Approve or supersede standard test procedures', 'any-psl'],
  ['STP.Delete', 'STP', 'Delete', 'Delete standard test procedures', 'any-psl'],
  ['VQCA.View', 'VQCA', 'View', 'View vendor quality agreements', 'record'],
  ['VQCA.Create', 'VQCA', 'Create', 'Create vendor quality agreements', 'record'],
  ['VQCA.Edit', 'VQCA', 'Edit', 'Edit vendor quality agreements', 'record'],
  ['VQCA.SignGql', 'VQCA', 'SignGql', 'Sign as Global Quality Lead', 'record'],
  ['VQCA.SignCm', 'VQCA', 'SignCm', 'Sign as Category Manager', 'record'],
  ['VQCARequests.View', 'VQCA Requests', 'View', 'View VQCA requests', 'record'],
  ['VQCARequests.Create', 'VQCA Requests', 'Create', 'Create VQCA requests', 'record'],
  ['VQCARequests.Edit', 'VQCA Requests', 'Edit', 'Edit VQCA requests', 'record'],
  ['VQCARequests.Approve', 'VQCA Requests', 'Approve', 'Approve VQCA requests', 'record'],
  ['VQCARequests.Delete', 'VQCA Requests', 'Delete', 'Delete VQCA requests', 'record'],
  ['COA.View', 'COA', 'View', 'View certificates of analysis', 'record'],
  ['COA.Create', 'COA', 'Create', 'Create certificates of analysis', 'record'],
  ['COA.Edit', 'COA', 'Edit', 'Edit certificates of analysis', 'record'],
  ['COA.Review', 'COA', 'Review', 'Review certificates of analysis', 'record'],
  ['COA.Delete', 'COA', 'Delete', 'Delete certificates of analysis', 'record'],
  ['COA.Restore', 'COA', 'Restore', 'Restore deleted certificates of analysis', 'record'],
  ['COA.Export', 'COA', 'Export', 'Export certificates of analysis', 'record'],
  ['Samples.View', 'QA Lab', 'View', 'View QA samples', 'record'],
  ['Samples.Create', 'QA Lab', 'Create', 'Create QA samples', 'record'],
  ['Samples.Edit', 'QA Lab', 'Edit', 'Edit QA samples', 'record'],
  ['Samples.Approve', 'QA Lab', 'Approve', 'Approve QA samples', 'record'],
  ['Experiments.View', 'QA Lab', 'View', 'View experiments', 'record'],
  ['Experiments.Create', 'QA Lab', 'Create', 'Create experiments', 'record'],
  ['Experiments.Edit', 'QA Lab', 'Edit', 'Edit experiments', 'record'],
  ['Experiments.Approve', 'QA Lab', 'Approve', 'Review experiments', 'record'],
  ['QAReports.View', 'QA Lab', 'ViewReports', 'View QA reports', 'record'],
  ['QAReports.Create', 'QA Lab', 'CreateReports', 'Create QA reports', 'record'],
  ['QAReports.Edit', 'QA Lab', 'EditReports', 'Edit QA reports', 'record'],
  ['QAReports.Approve', 'QA Lab', 'ApproveReports', 'Approve QA reports', 'record'],
  ['QAReports.ApproveTestCertificate', 'QA Lab', 'ApproveCertificates', 'Approve QA test certificates', 'record'],
  ['COQ.View', 'COQ', 'View', 'View certificates of quality', 'record'],
  ['COQ.Generate', 'COQ', 'Generate', 'Generate certificates of quality', 'record'],
  ['COQ.Delete', 'COQ', 'Delete', 'Delete certificates of quality', 'record'],
  ['Attachments.View', 'Attachments', 'View', 'View record attachments', 'record'],
  ['Attachments.Create', 'Attachments', 'Create', 'Add record attachments', 'record'],
  ['Attachments.Delete', 'Attachments', 'Delete', 'Delete record attachments', 'record'],
  ['Packaging.View', 'Packaging', 'View', 'View packaging records', 'record'],
  ['Packaging.Create', 'Packaging', 'Create', 'Create packaging records', 'record'],
  ['Packaging.Edit', 'Packaging', 'Edit', 'Edit packaging records', 'record'],
  ['Packaging.ManageAssignments', 'Packaging', 'ManageAssignments', 'Manage packaging assignments', 'record'],
  ['Reports.View', 'Reports', 'View', 'View reports', 'record'],
  ['Reports.Export', 'Reports', 'Export', 'Export reports', 'record'],
  ['Reports.AdminView', 'Reports', 'AdminView', 'View administrator-only reports', 'global-only'],
  ['Hdfx.View', 'Hdfx', 'View', 'View HDFX', 'record'],
  ['Vendors.View', 'Vendors', 'View', 'View vendors', 'any-psl'],
  ['Vendors.Manage', 'Vendors', 'Manage', 'Manage vendors', 'any-psl'],
  ['Alerts.View', 'Alerts', 'View', 'View alerts', 'any-psl'],
  ['Alerts.Manage', 'Alerts', 'Manage', 'Manage alerts', 'global-only'],
  ['CategoryLevels.View', 'Category Levels', 'View', 'View category levels', 'any-psl'],
  ['CategoryLevels.Manage', 'Category Levels', 'Manage', 'Manage category levels', 'global-only'],
  ['ReferenceData.View', 'Reference Data', 'View', 'View reference data', 'any-psl'],
  ['ReferenceData.Manage', 'Reference Data', 'Manage', 'Manage reference data', 'global-only'],
  ['Administration.Users.View', 'Administration', 'ViewUsers', 'View users', 'any-psl'],
  ['Administration.Users.Manage', 'Administration', 'ManageUsers', 'Manage users', 'any-psl'],
  ['Administration.Roles.View', 'Administration', 'ViewRoles', 'View roles', 'any-psl'],
  ['Administration.Roles.Manage', 'Administration', 'ManageRoles', 'Manage roles and permissions', 'any-psl'],
  ['Administration.Assignments.Manage', 'Administration', 'ManageAssignments', 'Manage role assignments', 'any-psl'],
  ['Administration.Psls.Manage', 'Administration', 'ManagePsls', 'Manage PSLs', 'global-only'],
  ['Administration.Audit.View', 'Administration', 'ViewAudit', 'View access audit history', 'global-only'],
].map(([permissionKey, module, action, description, scopeRule]) => ({
  permissionKey: permissionKey as AccessPermissionKey,
  module: module as string,
  action: action as string,
  description: description as string,
  scopeRule: scopeRule as AccessScopeRule,
}));

export function normalizeAccessPermissions<Permission extends object>(permissions: Permission[]): Permission[] {
  const definitions = ACCESS_PERMISSIONS.filter((permission) => permission.permissionKey === 'Administration.Roles.Manage' || permission.module === 'STP');
  return permissions.map((permission) => {
    const definition = 'permissionKey' in permission ? definitions.find((entry) => entry.permissionKey === permission.permissionKey) : undefined;
    return definition ? { ...permission, ...definition } : permission;
  });
}